import {
  Ban,
  Check,
  CircleStop,
  Copy,
  Info,
  RotateCw,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';

import { levelName } from '../data/level-text';
import type {
  AttachedFile,
  InteractionAnswer,
  Message,
  MessageFeedback,
  ProposalDecision,
} from '../types';

import { ConnectCard } from './ConnectCard';
import { DataHealth } from './DataHealth';
import { InteractionCard } from './InteractionCard';
import { Markdown } from './Markdown';
import { MessageMeta } from './MessageMeta';
import { ProcessingIndicator } from './ProcessingIndicator';
import { ProposalCard } from './ProposalCard';
import { BlockList } from './blocks/BlockRenderer';
import { SentFiles } from './files/SentFiles';

interface MessageRowProps {
  message: Message;
  /** The message's conversation: its Connect cards answer there, whichever is active. */
  conversationId?: string;
  relatedFiles?: AttachedFile[];
  onFeedback: (id: string, feedback: MessageFeedback) => void;
  /** Absent when regenerating would resend the turn without its files. */
  onRegenerate?: () => void;
  answered?: boolean;
  onAnswerInteraction: (answer: InteractionAnswer) => void;
  onDecideProposal: (decision: ProposalDecision) => void;
  /** Removes a queued message. */
  onRemoveQueued?: (messageId: string) => void;
  /**
   * "Send now" for a queued message that waits for the user: `queueState` `stale`, or `held`
   * while a stop holds the queue (`waitsForSendNow`).
   */
  onSendNow?: () => void;
  onAnswerInternal?: () => void;
  onAskInNewChat?: () => void;
}

/**
 * One chat row. User messages are a dark pill aligned right; assistant messages
 * are left-aligned rich text followed by an action toolbar
 * (copy / like / dislike / regenerate), matching the Figma message design.
 */
export function MessageRow({
  message,
  conversationId,
  relatedFiles,
  onFeedback,
  onRegenerate,
  answered = false,
  onAnswerInteraction,
  onDecideProposal,
  onRemoveQueued,
  onSendNow,
  onAnswerInternal,
  onAskInNewChat,
}: MessageRowProps) {
  const { t } = useTranslation('ai-cio');
  const [copied, setCopied] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const refocusRowRef = useRef(false);
  const isUser = message.role === 'user';
  const isStreaming = message.status === 'streaming';
  const isError = message.status === 'error';
  const isCancelled = message.status === 'cancelled';
  const isStopped = message.status === 'stopped';
  const isQueued = message.status === 'queued';
  // It waits for the user: too old to run on its own, or a stop that raised no card holds it.
  const waitsForSendNow =
    message.queueState === 'stale' ||
    (message.queueState === 'held' && message.waitsForSendNow === true);
  const steps = message.steps ?? [];
  const hasSteps = steps.length > 0;
  const explainedFailure = isError && message.failure !== undefined;
  const hasText = message.content.replace(/<[^>]+>/g, '').trim() !== '';
  const isSpentHost = answered && !hasText;

  useLayoutEffect(() => {
    if (!refocusRowRef.current) return;
    const row = rowRef.current;
    const active = document.activeElement;
    if (row && active && row.contains(active)) return;
    refocusRowRef.current = false;
    if (!active || active === document.body) row?.focus();
  });

  function keepingFocus(action: () => void) {
    return () => {
      const active = document.activeElement;
      refocusRowRef.current = Boolean(active && rowRef.current?.contains(active));
      action();
    };
  }

  const regenerate = onRegenerate && keepingFocus(onRegenerate);
  const answerInternal = onAnswerInternal && keepingFocus(onAnswerInternal);

  async function handleCopy() {
    // Strip tags for a plain-text copy of assistant HTML.
    const text = message.content.replace(/<[^>]+>/g, '').trim();
    if (!text) return;

    let success = false;
    try {
      await navigator.clipboard.writeText(text);
      success = true;
    } catch {
      // Modern clipboard API failed (non-secure context or denied permission);
      // fall back to the legacy execCommand approach.
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        document.execCommand('copy');
        ta.remove();
        success = true;
      } catch {
        // Both methods failed — nothing else to do.
      }
    }

    if (success) {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 1500);
    }
  }

  if (isUser) {
    return (
      <div className="group flex flex-col items-end gap-1">
        <div className={cn('flex w-full flex-col items-end gap-2', isQueued && 'opacity-60')}>
          {message.files && message.files.length > 0 && (
            <SentFiles files={message.files} sentAt={message.createdAt} />
          )}
          {message.content && (
            <div className="max-w-[80%] whitespace-pre-wrap rounded-2xl bg-primary px-4 py-2.5 text-sm text-primary-foreground">
              {message.content}
            </div>
          )}
        </div>
        {isQueued ? (
          // Waiting for a turn: dimmed, with remove, until a turn takes it.
          <div className="flex max-w-[80%] items-center gap-1 text-xs text-muted-foreground">
            {message.queueNote ? (
              <Markdown className="text-xs text-destructive">{message.queueNote}</Markdown>
            ) : (
              <span>{t('queue.queued', 'Queued')}</span>
            )}
            {waitsForSendNow && onSendNow && (
              <button
                type="button"
                onClick={onSendNow}
                className="shrink-0 rounded-md px-1.5 py-0.5 font-medium text-primary transition-colors hover:bg-accent"
              >
                {t('queue.sendNow', 'Send now')}
              </button>
            )}
            {message.queueState !== 'sending' && onRemoveQueued && (
              <ActionButton
                label={t('queue.remove', 'Remove')}
                onClick={() => {
                  onRemoveQueued(message.id);
                }}
              >
                <X className="size-4" />
              </ActionButton>
            )}
          </div>
        ) : (
          <div className="flex items-center opacity-0 transition-opacity group-hover:opacity-100">
            <ActionButton
              label={copied ? t('actions.copied', 'Copied') : t('actions.copy', 'Copy')}
              active={copied}
              activeClassName="text-emerald-600"
              onClick={() => {
                void handleCopy();
              }}
            >
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            </ActionButton>
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={rowRef} tabIndex={-1} className="flex flex-col gap-1 outline-none">
      <MessageMeta message={message} onAskInNewChat={onAskInNewChat} />

      {/* The family of the turn's model has no level at the rank asked, so it ran at another. */}
      {message.modeNote && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Info className="size-3.5 shrink-0" aria-hidden />
          {t('level.changedNote', "Ran at {{level}}: {{requested}} isn't available on this model", {
            level: levelName(t, message.modeNote.level, message.modeNote.label),
            requested: levelName(t, message.modeNote.requested, message.modeNote.requestedLabel),
          })}
        </div>
      )}

      {hasSteps && (
        <ProcessingIndicator
          steps={steps}
          status={message.status}
          waiting={message.waiting}
          workedForMs={message.workedForMs}
          route={message.route}
          failure={message.failure}
          onRetry={regenerate}
          onAnswerInternal={answerInternal}
          className="mb-1"
        />
      )}

      {isError && !explainedFailure ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive"
        >
          <Markdown className="text-destructive">{message.content}</Markdown>
        </div>
      ) : message.content ? (
        <Markdown>{message.content}</Markdown>
      ) : (
        isStreaming &&
        !hasSteps && (
          <ThinkingDots
            label={t('actions.generating', 'Generating response')}
            status={message.statusText}
          />
        )
      )}

      {/* A reply cut off here (its chat was deleted or cleared) — neutral, not an error. */}
      {isCancelled && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Ban className="size-3.5" aria-hidden />
          {t('message.cancelled', 'Cancelled')}
        </div>
      )}

      {/* The user stopped it before it finished: what streamed stays, the line that nothing
          had finished included, and its stop card follows when it raised one. */}
      {isStopped && !hasSteps && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <CircleStop className="size-3.5" aria-hidden />
          {t('message.stopped', 'Stopped')}
        </div>
      )}

      {/* Under text already streamed: the AI CIO went back to work after writing. */}
      {isStreaming && message.content && message.statusText && !hasSteps ? (
        <ThinkingDots label={message.statusText} status={message.statusText} />
      ) : (
        isStreaming &&
        message.content && (
          <span className="inline-block h-4 w-1.5 animate-pulse rounded-sm bg-foreground/60" />
        )
      )}

      {/* Rich content blocks (tables, charts, metrics, …) */}
      {(Boolean(message.blocks?.length) || Boolean(message.pendingFiles?.length)) && (
        <BlockList
          blocks={message.blocks ?? []}
          order={message.blockOrder}
          pendingFiles={message.pendingFiles}
          onTryAgain={isStreaming ? undefined : regenerate}
        />
      )}

      {/* What the answer's data lacks, what its figures cover, and its sources. */}
      <DataHealth health={message.health} sources={message.sources} />

      {/* Open clarifying question (MCQ) */}
      {message.pendingInteraction && (
        <div className="mt-2">
          <InteractionCard question={message.pendingInteraction} onAnswer={onAnswerInteraction} />
        </div>
      )}

      {message.pendingProposal && (
        <div className="mt-2">
          <ProposalCard
            proposal={message.pendingProposal}
            relatedFiles={relatedFiles}
            onDecide={onDecideProposal}
          />
        </div>
      )}

      {/* Connect cards, where they arrived; an answered or closed one stays as its record. */}
      {message.connectCards?.map((card) => (
        <div key={card.proposal.proposalId} className="mt-2">
          <ConnectCard card={card} conversationId={conversationId} />
        </div>
      ))}

      {!isStreaming &&
        !explainedFailure &&
        !message.pendingInteraction &&
        !message.pendingProposal &&
        !message.connectCards?.some((c) => !c.record) && (
          <div className="flex items-center">
            {!isSpentHost && (
              <ActionButton
                label={copied ? t('actions.copied', 'Copied') : t('actions.copy', 'Copy')}
                active={copied}
                activeClassName="text-emerald-600"
                onClick={() => {
                  void handleCopy();
                }}
              >
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </ActionButton>
            )}
            <ActionButton
              label={t('actions.like', 'Good response')}
              active={message.feedback === 'like'}
              activeClassName="text-primary"
              onClick={() => {
                onFeedback(message.id, 'like');
              }}
            >
              <ThumbsUp className={cn('size-4', message.feedback === 'like' && 'fill-current')} />
            </ActionButton>
            <ActionButton
              label={t('actions.dislike', 'Bad response')}
              active={message.feedback === 'dislike'}
              activeClassName="text-destructive"
              onClick={() => {
                onFeedback(message.id, 'dislike');
              }}
            >
              <ThumbsDown
                className={cn('size-4', message.feedback === 'dislike' && 'fill-current')}
              />
            </ActionButton>
            {regenerate && !isSpentHost && (
              <ActionButton label={t('actions.regenerate', 'Regenerate')} onClick={regenerate}>
                <RotateCw className="size-4" />
              </ActionButton>
            )}
          </div>
        )}
    </div>
  );
}

interface ActionButtonProps {
  label: string;
  active?: boolean;
  /** Extra classes applied when `active` (e.g. a color for like/dislike/copy). */
  activeClassName?: string;
  onClick: () => void;
  children: ReactNode;
}

function ActionButton({ label, active, activeClassName, onClick, children }: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'flex size-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
        active && (activeClassName ?? 'text-foreground'),
      )}
    >
      {children}
    </button>
  );
}

/**
 * The working indicator. With a status line it shows the line too: plain text,
 * one line, cut with an ellipsis, as the orchestrator composed it.
 */
export function ThinkingDots({ label, status }: { label: string; status?: string }) {
  return (
    <div className="flex min-w-0 items-center gap-1 py-1" aria-label={status ?? label}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="size-1.5 shrink-0 animate-pulse rounded-full bg-muted-foreground/60"
          style={{ animationDelay: `${(i * 150).toString()}ms` }}
        />
      ))}
      {status && (
        <span className="ms-1.5 truncate text-sm text-muted-foreground" aria-live="polite">
          {status}
        </span>
      )}
    </div>
  );
}
